import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { withFlowType } from 'helpers/assistantFlow';

/**
 * Hook for choosing the upload JD option
 */
export const useChooseUploadJD = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post('/assessment/hiring-assistant/', withFlowType({
        assessment_id: assessmentId,
        input: "upload",
      }));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hiringAssistant', assessmentId] });
      queryClient.invalidateQueries({ queryKey: ['conversationSessions'] });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to choose upload option';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    uploadJDResponse: mutation.data,
    isLoading: mutation.isPending,
    error: mutation.error,
    chooseUploadJD: mutation.mutate,
  };
};

/**
 * Hook for uploading a job description
 */
export const useUploadJobDescription = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (jobDescription: File) => {
      const formData = new FormData();
      formData.append('assessment_id', assessmentId || '');
      formData.append('file', jobDescription);

      // Use apiClient so the baseURL/path join is normalized (avoids a
      // double slash when REACT_APP_API_BASE_URL has a trailing slash, which
      // 404s on production). Auth is added by the instance's request interceptor.
      const response = await apiClient.post(
        '/assessment/hiring-assistant/',
        withFlowType(formData),
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hiringAssistant', assessmentId] });
      queryClient.invalidateQueries({ queryKey: ['conversationSessions'] });
      openToast({
        show: true,
        heading: 'Success',
        text: 'Job description uploaded successfully!',
        type: 'info',
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to upload job description';
      openToast({
        show: true,
        heading: 'Sorry',
        text: errorMessage,
        type: 'error',
        timeOut: 5000,
      });
    },
  });

  return {
    uploadResponse: mutation.data,
    isLoading: mutation.isPending,
    error: mutation.error,
    uploadJobDescription: mutation.mutate,
  };
};
